import { Injectable } from '@angular/core';
import { Observable, combineLatest, of } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';

import { PaymentAccountService } from './payment-account/payment-account.service';
import { PaymentAccount } from './payment-account/model/payment-account';
import { PaymentCardProvider } from './payment-card-provider/model/payment-card-provider';
import { CompanySettingsServiceHelper } from '../settings/services/company-settings-helper.service';

@Injectable()
export class PaymentsService {
  constructor(
    private companySettingsServiceHelper: CompanySettingsServiceHelper,
    private paymentAccountService: PaymentAccountService
  ) {}

  public isProcessorsEnabled(): Observable<boolean> {
    return combineLatest(
      this.companySettingsServiceHelper.isCompanyIn('us'),
      this.companySettingsServiceHelper.isCompanyIn('canada'),
      this.companySettingsServiceHelper.isCompanyIn('belgium'),
      this.companySettingsServiceHelper.isCompanyIn('netherlands'),
      this.companySettingsServiceHelper.isCompanyIn('united kingdom'),
      this.paymentAccountService.paymentAccountChanged
    ).pipe(
      map(
        (
          value: [boolean, boolean, boolean, boolean, boolean, PaymentAccount]
        ) => {
          return value[0] || value[1] || value[2] || value[3] || value[4];
        }
      )
    );
  }

  public isLightspeedPaymentEnabled(): Observable<boolean> {
    return this.paymentAccountService.paymentAccountChanged.pipe(
      map((paymentAccount: PaymentAccount) => {
        if (paymentAccount) {
          return (
            PaymentCardProvider.isLightspeedPayment(paymentAccount.provider) ||
            PaymentCardProvider.isLightspeedPaymentsCayan(
              paymentAccount.provider
            ) ||
            PaymentCardProvider.isLightspeedPaymentsAdyen(
              paymentAccount.provider
            )
          );
        }
        return false;
      })
    );
  }

  public isTerminalsEnabled(): Observable<boolean> {
    return combineLatest(
      this.companySettingsServiceHelper.isCompanyIn('us'),
      this.companySettingsServiceHelper.isCompanyIn('uk'),
      this.companySettingsServiceHelper.isCompanyIn('canada'),
      this.companySettingsServiceHelper.isCompanyIn('belgium'),
      this.companySettingsServiceHelper.isCompanyIn('netherlands'),
      this.companySettingsServiceHelper.isCompanyIn('united kingdom'),
      this.paymentAccountService.paymentAccountChanged
    ).pipe(
      map(
        ([
          isUs,
          isUk,
          isCanada,
          isBelgium,
          isNetherlands,
          isUK,
          paymentAccount
        ]: [
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
          PaymentAccount
        ]) => {
          if (
            paymentAccount &&
            paymentAccount.provider &&
            PaymentCardProvider.isLightspeedPayment(paymentAccount.provider)
          ) {
            return false;
          }

          return (
            (isUs && paymentAccount !== null) ||
            isUk ||
            ((isBelgium || isNetherlands || isUK || isCanada) &&
              paymentAccount !== null &&
              PaymentCardProvider.isLightspeedPaymentsAdyen(
                paymentAccount.provider
              )) ||
            (isCanada &&
              paymentAccount !== null &&
              (PaymentCardProvider.isFirstData(paymentAccount.provider) ||
                PaymentCardProvider.isMoneris(paymentAccount.provider)))
          );
        }
      )
    );
  }

  public isProcessingEnabled(): Observable<boolean> {
    return combineLatest(
      this.companySettingsServiceHelper.isCompanyIn('canada'),
      this.companySettingsServiceHelper.isCompanyIn('us'),
      this.companySettingsServiceHelper.isCompanyIn('belgium'),
      this.companySettingsServiceHelper.isCompanyIn('netherlands'),
      this.companySettingsServiceHelper.isCompanyIn('united kingdom'),
      this.paymentAccountService.paymentAccountChanged
    ).pipe(
      map(
        ([isCanada, isUs, isBelgium, isNetherlands, isUK, paymentAccount]: [
          boolean,
          boolean,
          boolean,
          boolean,
          boolean,
          PaymentAccount
        ]) => {
          if (
            paymentAccount &&
            paymentAccount.provider &&
            PaymentCardProvider.isLightspeedPayment(paymentAccount.provider)
          ) {
            return false;
          }
          return isCanada || isUs || isBelgium || isNetherlands || isUK;
        }
      ),
      flatMap((show: boolean) => {
        if (show) {
          return this.paymentAccountService.getPaymentAccount();
        } else {
          return of(null);
        }
      }),
      map((paymentAccount: PaymentAccount) => {
        return (
          paymentAccount &&
          paymentAccount.provider.type &&
          !PaymentCardProvider.isFirstData(paymentAccount.provider) &&
          !PaymentCardProvider.isMoneris(paymentAccount.provider) &&
          !PaymentCardProvider.isVantivDejavoo(paymentAccount.provider)
        );
      })
    );
  }
}
